import axios from 'axios';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { ITableServicePort } from '@onboarding/application/ports';
import type { TableServiceDto } from '@onboarding/application/dtos';
import { createTableServiceConfig } from '@onboarding/domain/value-objects';

const onbLog = logger.forDomain('ONB');
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for applying table service configuration.
 *
 * Flow: TableServiceDto → domain value object (validated) → persist settings + create areas/tables
 *
 * @param props.tableServicePort - ITableServicePort implementation (injected)
 * @returns Use case executor
 *
 * @example
 * const useCase = applyTableServiceUseCase({ tableServicePort });
 * await useCase.execute({ hasTableService: true, areas: [{ name: 'Main', tableCount: 5, prefix: 'M' }] });
 */
export const applyTableServiceUseCase = (props: { tableServicePort: ITableServicePort }) => {
  const { tableServicePort } = props;

  const execute = async (dto: TableServiceDto): Promise<void> => {
    const span = tracer.startSpan('ONB.apply_table_service');
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const config = createTableServiceConfig({
          hasTableService: dto.hasTableService,
          areas: dto.areas,
        });

        span.setAttribute('onb.has_table_service', config.hasTableService);
        span.setAttribute('onb.area_count', config.areas.length);

        await tableServicePort.applyTableServiceSettings(config);

        let newAreaCount = 0;
        if (config.hasTableService && config.areas.length > 0) {
          const existingNames = await tableServicePort.getExistingAreaNames();
          const existingSet = new Set(existingNames.map((n) => n.trim().toLowerCase()));
          const newAreas = config.areas.filter((area) => !existingSet.has(area.name.trim().toLowerCase()));
          newAreaCount = newAreas.length;

          if (newAreas.length > 0) {
            await tableServicePort.setupAreasWithTables(newAreas);
          }
        }

        onbLog.info('table_service_applied', {
          'onb.has_table_service': config.hasTableService,
          'onb.area_count': config.areas.length,
          'onb.new_area_count': newAreaCount,
        });
      } catch (error) {
        onbLog.error('table_service_apply_failed', {
          'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
          'error.message': error instanceof Error ? error.message : String(error),
          'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
          'onb.has_table_service': dto.hasTableService,
          'onb.area_count': dto.areas.length,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
